import { MouseEvent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { config } from "node-config-ts";
import { DEFAULT_AXNUI_CONTENT, DEFAULT_LANGUAGE } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    handleContentMerging,
    loadLanguageFile,
    isFulfilled,
    isRejected,
    extractConfigurationContentForLocaleType
} from "@iddataweb/idw-common-ui/utils";
import { BUCKET_NAME } from "@constants/language-config";
import {
    getLocalizeStrings,
    getAvailableLanguages,
    getGeneralTheme,
    getConfigurationContent,
    getMergeThemingAndLocalizedFile,
    getLocalizeFileNamespace
} from "@redux/selectors/verification-configuration-selectors";
import { getActiveLanguage } from "@redux/selectors/language-selectors";
import { DEFAULT_NAMESPACE } from "@constants/form-constants";
import { Logger } from "@utils/logger";
import { setActiveLanguage, setLanguageContent } from "@redux/actions/language-actions";
import styles from "./language-selector-container.less";

type LanguageSelectorContainerProps = {
    useThemedUI: boolean;
}

export const LanguageSelectorContainer = (props: LanguageSelectorContainerProps) => {
    const { useThemedUI } = props;
    const dispatch = useDispatch();
    const localizeFileNamespace = useSelector(getLocalizeFileNamespace);
    const localizeStrings = useSelector(getLocalizeStrings);
    const availableLanguages = useSelector(getAvailableLanguages);
    const activeLanguage = useSelector(getActiveLanguage);
    const generalTheme = useSelector(getGeneralTheme);
    const configurationContent = useSelector(getConfigurationContent);
    const mergeThemingAndLocalizedFile = useSelector(getMergeThemingAndLocalizedFile);

    const supportedLanguages = useMemo(() => {
        if (availableLanguages) {
            return availableLanguages;
        }
        return [];
    }, [availableLanguages]);




    const onLanguageChange = async (e: MouseEvent, language: { code: LocaleTypes, name: string }) => {
        e.preventDefault();
        if (e.button === 0) {
            const namespaceToUse = localizeFileNamespace || DEFAULT_NAMESPACE;

            const [namespaceLanguageResponse] = await Promise.allSettled([
                namespaceToUse !== DEFAULT_NAMESPACE
                    ? loadLanguageFile(BUCKET_NAME, language.code, namespaceToUse, config.APPLICATION_ENVIRONMENT)
                    : Promise.reject("No namespace language file is required")
            ]);

            let namespaceLanguageContent;
            if (isFulfilled(namespaceLanguageResponse)) {
                namespaceLanguageContent = namespaceLanguageResponse.value;
            } else if (isRejected(namespaceLanguageResponse)) {
                console.log(namespaceLanguageResponse.reason);
                namespaceLanguageContent = {};
            }

            Logger.info(`[App] Language set to ${language.code}`);
            Logger.info(`[App] Namespace set to ${namespaceToUse}`);

            const finalConfigurationContent = extractConfigurationContentForLocaleType(
                DEFAULT_AXNUI_CONTENT,
                configurationContent,
                language.code
            );

            const mergedContent = handleContentMerging(
                localizeStrings,
                mergeThemingAndLocalizedFile,
                DEFAULT_AXNUI_CONTENT[DEFAULT_LANGUAGE],
                DEFAULT_AXNUI_CONTENT[language.code] || DEFAULT_AXNUI_CONTENT[DEFAULT_LANGUAGE],
                namespaceLanguageContent,
                finalConfigurationContent.data
            );

            dispatch(setLanguageContent(mergedContent));
            dispatch(setActiveLanguage(language.code));
        }
    };

    if (!localizeStrings) {
        return null;
    }

    const linkStyle = useThemedUI ? {
        color: generalTheme.linksColor
    } : undefined;

    return (
        <div className={styles.languageSelectorContainer}>
            {supportedLanguages.map((language, index) => {
                return language.code !== activeLanguage ? (
                    <a
                        style={linkStyle}
                        key={`${language.code}-${index}`}
                        className={styles.link}
                        href=""
                        onClick={(event: MouseEvent) => onLanguageChange(event, language)}
                    >
                        {language.name}
                    </a>
                ) : (
                    <span className={styles.selectedTextSpan} key={`${language.code}-${index}`}>{language.name}</span>
                );
            })}
        </div>
    );
};